<template>
  <div>
    <div v-if="$router.currentRoute.name === 'advanced-ev-chargers-charger'">
      <div class="pr-2">
        <v-breadcrumbs :items="breadcrumbs">
          <template v-slot:divider>
            <v-icon>chevron_right</v-icon>
          </template>
        </v-breadcrumbs>
      </div>

      <h1 class="mb-4 text-xs-center">{{ isNewRecord ? 'Create EV Charger' : 'Edit EV Charger' }}</h1>

      <v-layout row justify-center>
        <v-flex xs12 sm10 md8 lg7>
          <v-tabs v-if="!isNewRecord" v-model="activeChargerTab" class="mb-3" @change="onChargerTabChanged">
            <v-tabs-slider color="primary" />
            <v-tab ripple>Details</v-tab>
            <v-tab ripple>Charging History</v-tab>
          </v-tabs>

          <v-card v-show="activeChargerTab === 0 || isNewRecord" class="mb-4">
            <v-card-text>
              <v-form ref="form">
                <v-layout row wrap mb-1>
                  <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                    Charge Point ID
                  </v-flex>
                  <v-flex sm8>
                    <v-text-field
                      v-model="form.chargePointId"
                      placeholder="Enter charge point ID"
                      :disabled="!isAdmin || apiInProgress || !isNewRecord"
                    />
                  </v-flex>
                </v-layout>

                <v-layout row wrap mb-1>
                  <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                    Agency
                  </v-flex>
                  <v-flex sm8>
                    <v-combobox
                      v-model="form.agencyId"
                      :items="agencyOptions"
                      placeholder="Select agency"
                      clearable
                      :disabled="!isAdmin || apiInProgress"
                    />
                  </v-flex>
                </v-layout>

                <v-layout row wrap mb-1>
                  <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                    Status
                  </v-flex>
                  <v-flex sm8>
                    <v-select
                      v-model="form.status"
                      :items="statusOptions"
                      placeholder="Select status"
                      :disabled="!isAdmin || apiInProgress"
                    />
                  </v-flex>
                </v-layout>

                <v-layout row wrap mb-1>
                  <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                    Location
                  </v-flex>
                  <v-flex sm8>
                    <v-btn
                      color="primary"
                      outline
                      small
                      class="ml-0"
                      :disabled="!isAdmin || apiInProgress"
                      @click="openLocationPicker"
                    >
                      <v-icon small left>fal fa-search-location</v-icon>
                      Search
                    </v-btn>
                    <span class="grey--text text--darken-1 ml-2">
                      Search by bus stop/address, or click the map to choose coordinates.
                    </span>
                  </v-flex>
                </v-layout>

                <v-layout row wrap mb-1>
                  <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                    Latitude
                  </v-flex>
                  <v-flex sm8>
                    <v-text-field
                      v-model.number="form.latitude"
                      type="number"
                      step="any"
                      placeholder="e.g. 5.6037"
                      :disabled="!isAdmin || apiInProgress"
                    />
                  </v-flex>
                </v-layout>

                <v-layout row wrap mb-1>
                  <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                    Longitude
                  </v-flex>
                  <v-flex sm8>
                    <v-text-field
                      v-model.number="form.longitude"
                      type="number"
                      step="any"
                      placeholder="e.g. -0.1870"
                      :disabled="!isAdmin || apiInProgress"
                    />
                  </v-flex>
                </v-layout>

                <v-layout row wrap mb-1>
                  <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                    Location Label
                  </v-flex>
                  <v-flex sm8>
                    <v-text-field
                      v-model="form.locationLabel"
                      placeholder="Optional location label"
                      :disabled="!isAdmin || apiInProgress"
                    />
                  </v-flex>
                </v-layout>

                <v-layout row wrap mb-1>
                  <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                    Timezone
                  </v-flex>
                  <v-flex sm8>
                    <timezone-selector
                      v-model="form.timezone"
                      :child-props="{
                        outline: true,
                        label: 'Select/search for a timezone',
                        disabled: !isAdmin || apiInProgress
                      }"
                    />
                  </v-flex>
                </v-layout>

                <v-divider class="my-3" />

                <v-layout row wrap mb-1>
                  <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                    Reported by Charger (OCPP)
                  </v-flex>
                  <v-flex sm8>
                    <v-list dense class="pa-0">
                      <v-list-tile>
                        <v-list-tile-content>Vendor</v-list-tile-content>
                        <v-list-tile-content class="text-xs-right">{{ form.vendor || '-' }}</v-list-tile-content>
                      </v-list-tile>
                      <v-list-tile>
                        <v-list-tile-content>Model</v-list-tile-content>
                        <v-list-tile-content class="text-xs-right">{{ form.model || '-' }}</v-list-tile-content>
                      </v-list-tile>
                      <v-list-tile>
                        <v-list-tile-content>Serial Number</v-list-tile-content>
                        <v-list-tile-content class="text-xs-right">{{ form.serialNumber || '-' }}</v-list-tile-content>
                      </v-list-tile>
                      <v-list-tile>
                        <v-list-tile-content>Firmware Version</v-list-tile-content>
                        <v-list-tile-content class="text-xs-right">{{ form.firmwareVersion || '-' }}</v-list-tile-content>
                      </v-list-tile>
                      <v-list-tile>
                        <v-list-tile-content>ICCID</v-list-tile-content>
                        <v-list-tile-content class="text-xs-right">{{ form.iccid || '-' }}</v-list-tile-content>
                      </v-list-tile>
                      <v-list-tile>
                        <v-list-tile-content>IMSI</v-list-tile-content>
                        <v-list-tile-content class="text-xs-right">{{ form.imsi || '-' }}</v-list-tile-content>
                      </v-list-tile>
                      <v-list-tile>
                        <v-list-tile-content>Last Boot</v-list-tile-content>
                        <v-list-tile-content class="text-xs-right">{{ formatDateTime(form.lastBootAt, form.timezone) }}</v-list-tile-content>
                      </v-list-tile>
                      <v-list-tile>
                        <v-list-tile-content>Last Seen</v-list-tile-content>
                        <v-list-tile-content class="text-xs-right">{{ formatDateTime(form.lastSeenAt, form.timezone) }}</v-list-tile-content>
                      </v-list-tile>
                    </v-list>
                  </v-flex>
                </v-layout>

                <v-divider class="my-3" />

                <template v-if="!isNewRecord">
                  <v-layout row wrap mb-1>
                    <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                      Connection Credential
                    </v-flex>
                    <v-flex sm8>
                      <v-alert :value="true" :type="form.hasAuthToken ? 'info' : 'warning'" outline>
                        Credential status: <strong>{{ form.hasAuthToken ? 'Set' : 'Not set' }}</strong>
                      </v-alert>
                    </v-flex>
                  </v-layout>

                  <v-layout row wrap mb-1>
                    <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                      New Password
                    </v-flex>
                    <v-flex sm8>
                      <v-text-field
                        v-model="credentialForm.password"
                        :type="credentialForm.showPassword ? 'text' : 'password'"
                        :append-icon="credentialForm.showPassword ? 'fal fa-eye-slash' : 'fal fa-eye'"
                        @click:append="credentialForm.showPassword = !credentialForm.showPassword"
                        @blur="onCredentialPasswordBlur"
                        placeholder="Enter new password"
                        :error-messages="credentialPasswordErrors"
                        :disabled="!isAdmin || apiInProgress"
                      />
                    </v-flex>
                  </v-layout>

                  <v-layout row wrap mb-1>
                    <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                      Confirm Password
                    </v-flex>
                    <v-flex sm8>
                      <v-text-field
                        v-model="credentialForm.confirmPassword"
                        :type="credentialForm.showPassword ? 'text' : 'password'"
                        placeholder="Re-enter password"
                        :error-messages="credentialConfirmPasswordErrors"
                        :disabled="!isAdmin || apiInProgress"
                        @blur="onCredentialConfirmPasswordBlur"
                      />
                    </v-flex>
                  </v-layout>

                  <v-layout row wrap mb-1>
                    <v-flex sm4 />
                    <v-flex sm8>
                      <v-checkbox
                        v-model="credentialForm.pushToCharger"
                        :disabled="!isAdmin || apiInProgress || !form.hasAuthToken"
                        label="Push to connected charger (ChangeConfiguration)"
                        hide-details
                      />
                    </v-flex>
                  </v-layout>

                  <v-layout row wrap mb-1 v-if="isStagingOrLocal">
                    <v-flex sm4 />
                    <v-flex sm8>
                      <v-btn
                        small
                        :disabled="!isAdmin || apiInProgress"
                        @click="populateDebugBootData"
                      >
                        Populate Stub Data (Staging/Local)
                      </v-btn>
                    </v-flex>
                  </v-layout>

                  <v-layout row wrap mb-1 v-if="canUseEmulator">
                    <v-flex sm4 />
                    <v-flex sm8>
                      <v-btn
                        small
                        outline
                        color="primary"
                        :disabled="apiInProgress"
                        @click="openEmulatorDialog"
                      >
                        <v-icon small left>fal fa-charging-station</v-icon>
                        Open Charger Simulator
                      </v-btn>
                    </v-flex>
                  </v-layout>

                  <v-layout row wrap mb-1>
                    <v-flex sm4 />
                    <v-flex sm8>
                      <div class="charger-credential-actions">
                        <v-btn
                          color="warning"
                          flat
                          small
                          :disabled="!isAdmin || apiInProgress"
                          @click="clearCredential"
                        >
                          Clear Credential
                        </v-btn>
                        <v-btn
                          color="primary"
                          small
                          :disabled="!isAdmin || apiInProgress"
                          @click="saveCredential"
                        >
                          {{ form.hasAuthToken ? 'Rotate Credential' : 'Set Initial Credential' }}
                        </v-btn>
                      </div>
                    </v-flex>
                  </v-layout>
                </template>
              </v-form>
            </v-card-text>

            <v-divider />

            <v-card-actions class="charger-form-actions">
              <v-btn flat color="warning" :disabled="apiInProgress" @click="navigateToList()">Cancel</v-btn>
              <v-spacer />
              <v-btn
                v-if="!isNewRecord"
                color="warning"
                flat
                :disabled="!isAdmin || apiInProgress"
                @click="deleteRecord"
              >
                Delete
              </v-btn>
              <v-btn
                color="primary"
                :disabled="!isAdmin || apiInProgress"
                @click="saveRecord"
              >
                Save
              </v-btn>
            </v-card-actions>
          </v-card>

          <v-card v-if="!isNewRecord" v-show="activeChargerTab === 1" class="mb-4">
            <v-card-title>
              <span class="title">Charging History</span>
              <v-spacer />
              <v-btn small flat :loading="historyLoading" @click="refreshChargingHistory">Refresh</v-btn>
            </v-card-title>
            <v-card-text>
              <v-layout row wrap>
                <v-flex xs12 sm4 pa-2>
                  <v-text-field
                    v-model="historyFilters.licensePlate"
                    label="License plate"
                    clearable
                    @keyup.enter.native="resetHistoryPageAndRefresh"
                  />
                </v-flex>
                <v-flex xs12 sm4 pa-2>
                  <st-date-field
                    id="ev-charger-history-date-from"
                    v-model="historyFilters.dateFrom"
                    label="From"
                    to-date-id="ev-charger-history-date-to"
                    @change="resetHistoryPageAndRefresh"
                  />
                </v-flex>
                <v-flex xs12 sm4 pa-2>
                  <st-date-field
                    id="ev-charger-history-date-to"
                    v-model="historyFilters.dateTo"
                    label="To"
                    from-date-id="ev-charger-history-date-from"
                    @change="resetHistoryPageAndRefresh"
                  />
                </v-flex>
              </v-layout>
              <v-layout row justify-end>
                <v-btn small color="primary" :loading="historyLoading" @click="resetHistoryPageAndRefresh">
                  Search
                </v-btn>
              </v-layout>
            </v-card-text>
            <v-data-table
              :headers="historyHeaders"
              :items="historyRecords"
              :loading="historyLoading"
              :pagination.sync="historyPagination"
              :total-items="historyTotalRecords"
              :rows-per-page-items="[10, 25, 50]"
              @update:pagination="refreshChargingHistory"
            >
              <template v-slot:items="{ item }">
                <tr>
                  <td>{{ formatDateTime(item.date_created, form.timezone) }}</td>
                  <td>{{ item.license_plate_number || '-' }}</td>
                  <td>{{ item.id_tag || '-' }}</td>
                  <td>{{ formatDateTime(item.start_time, form.timezone) }}</td>
                  <td>{{ formatDateTime(item.stop_time, form.timezone) }}</td>
                  <td>{{ formatKwh(item.energy_delivered_kwh) }}</td>
                  <td>{{ item.status || '-' }}</td>
                </tr>
              </template>
            </v-data-table>
          </v-card>
        </v-flex>
      </v-layout>

      <v-dialog v-model="locationPickerDialog" max-width="1100px" persistent content-class="location-picker-dialog">
        <v-card class="location-picker-dialog-card">
          <v-toolbar ref="locationPickerToolbar" dense flat color="primary" dark>
            <v-toolbar-title>Select Charger Location</v-toolbar-title>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn icon small dark class="ml-2" v-on="on">
                  <v-icon small>fal fa-info-circle</v-icon>
                </v-btn>
              </template>
              <span>
                Search for a bus stop or address, select a bus stop marker, or click anywhere on the map.
                Click Apply to copy the selected coordinates into the form.
              </span>
            </v-tooltip>
            <v-spacer />
            <v-btn icon dark @click="closeLocationPicker">
              <v-icon>fal fa-times</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text class="pa-3 location-picker-dialog-content">
            <div class="location-picker-map">
              <st-map
                v-if="stMapApis && locationPickerMapShouldRender"
                single-location-search
                topbar
                is-modal
                disable-map-hash
                container-id="evChargerLocationPickerMap"
                :height-offset="locationPickerMapHeightOffset"
                :default-center="locationPickerCenter"
                :default-zoom="15"
                :mapbox-access-token="mapboxAccessToken"
                :maptiler-key="maptilerKey"
                :marker-options="markerOptions"
                :api="stMapApis"
                :on-map-loaded="onLocationPickerMapLoaded"
                :on-location-selected="onLocationPickerLocationSelected"
                :on-map-clicked="onLocationPickerMapClicked"
                :on-bus-stop-clicked="onLocationPickerBusStopClicked"
              />
            </div>

            <v-layout ref="locationPickerSummary" row wrap mt-3>
              <v-flex xs12 sm4 pa-1>
                <strong>Latitude:</strong>
                {{ locationPickerSelection ? locationPickerSelection.latitude : '-' }}
              </v-flex>
              <v-flex xs12 sm4 pa-1>
                <strong>Longitude:</strong>
                {{ locationPickerSelection ? locationPickerSelection.longitude : '-' }}
              </v-flex>
              <v-flex xs12 sm4 pa-1>
                <strong>Label:</strong>
                {{ locationPickerSelection && locationPickerSelection.label ? locationPickerSelection.label : '-' }}
              </v-flex>
            </v-layout>
          </v-card-text>
          <v-card-actions ref="locationPickerActions" class="location-picker-dialog-actions">
            <v-btn flat color="warning" @click="closeLocationPicker">Cancel</v-btn>
            <v-spacer />
            <v-btn color="primary" :disabled="!locationPickerSelection" @click="applyLocationPickerSelection">
              Apply Location
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <ocpp-emulator-dialog
        v-model="emulatorDialogVisible"
        :charge-point-id="form.chargePointId"
      />
    </div>
  </div>
</template>

<script>
import src from './src'

export default src
</script>

<style scoped>
.charger-credential-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.charger-form-actions {
  flex-wrap: wrap;
  padding: 8px 16px 16px;
}

.location-picker-dialog-card {
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.location-picker-dialog-content {
  flex: 1 1 auto;
  overflow-y: auto;
}

.location-picker-dialog-actions {
  flex: 0 0 auto;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.location-picker-map {
  min-height: 300px;
}
</style>
